import { PayableRow } from '@bariendo/lib/payables';
import { Box, Button, Group, Popover, Stack, Text, TextInput } from '@mantine/core';
import dayjs from 'dayjs';
import { ReactElement, useState } from 'react';
import { alert } from '@/services/alert';
import { formatLedgerMoney } from '../ledger/ledgerRows';
import { sumActual } from './payableRows';
import { PayableActions } from './usePayableActions';

/**
 * The bar over the lines once some are selected: Mark paid (BAR-3046), Mark unpaid for Paid lines
 * (BAR-3125), and Clear.
 */
export function MarkPaidBar({
  selected,
  paid,
  actions,
  onClear,
  now,
}: {
  /** Open lines that can be paid. */
  selected: PayableRow[];
  /** Paid lines, to mark unpaid. */
  paid: PayableRow[];
  actions: PayableActions;
  onClear: () => void;
  now: Date;
}): ReactElement | null {
  const [opened, setOpened] = useState(false);
  const [unpaying, setUnpaying] = useState(false);
  const all = [...selected, ...paid];
  if (!all.length) {
    // Reset here, or the next selection opens the form without a click.
    if (opened) setOpened(false);
    return null;
  }

  const markUnpaid = async (): Promise<void> => {
    setUnpaying(true);
    try {
      await actions.markUnpaid(paid);
      onClear();
    } catch (error) {
      alert.error(`Couldn't mark the lines unpaid: ${error instanceof Error ? error.message : 'unknown error'}`);
    } finally {
      setUnpaying(false);
    }
  };

  return (
    <Group gap="sm" px={16} py={8}>
      <Text fz={13}>
        <b>{all.length}</b> selected · <b>{formatLedgerMoney(sumActual(all))}</b>
      </Text>
      {selected.length > 0 && (
        <Popover
          opened={opened}
          onChange={setOpened}
          position="bottom-start"
          width={280}
          withinPortal
          shadow="md"
          transitionProps={{ duration: 0 }}
        >
          <Popover.Target>
            <Button size="xs" color="dark" onClick={() => setOpened((open) => !open)}>
              Mark paid
            </Button>
          </Popover.Target>
          <Popover.Dropdown>
            {opened && (
              <MarkPaidForm
                selected={selected}
                actions={actions}
                now={now}
                onDone={() => {
                  setOpened(false);
                  onClear();
                }}
                onCancel={() => setOpened(false)}
              />
            )}
          </Popover.Dropdown>
        </Popover>
      )}
      {paid.length > 0 && (
        <Button size="xs" variant="default" loading={unpaying} onClick={() => void markUnpaid()}>
          Mark unpaid
        </Button>
      )}
      <Button size="xs" variant="subtle" color="gray" onClick={onClear}>
        Clear
      </Button>
    </Group>
  );
}

function MarkPaidForm({
  selected,
  actions,
  now,
  onDone,
  onCancel,
}: {
  selected: PayableRow[];
  actions: PayableActions;
  now: Date;
  onDone: () => void;
  onCancel: () => void;
}): ReactElement {
  const [paidOn, setPaidOn] = useState(dayjs(now).format('YYYY-MM-DD'));
  const [reference, setReference] = useState('');
  const [saving, setSaving] = useState(false);

  const save = async (): Promise<void> => {
    setSaving(true);
    try {
      await actions.markPaid(selected, { paidOn, reference });
      onDone();
    } catch (error) {
      alert.error(`Couldn't mark the lines paid: ${error instanceof Error ? error.message : 'unknown error'}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Stack gap={8}>
      <Text fz={13} fw={600}>
        {`Mark ${selected.length} ${selected.length === 1 ? 'line' : 'lines'} paid`}
      </Text>
      <TextInput
        label="Paid on"
        size="xs"
        type="date"
        value={paidOn}
        onChange={(event) => setPaidOn(event.currentTarget.value)}
      />
      <TextInput
        label="Payment reference"
        size="xs"
        placeholder="ACH or check number"
        value={reference}
        onChange={(event) => setReference(event.currentTarget.value)}
      />
      <Box>
        <Group justify="flex-end" gap={8} mt={4}>
          <Button size="xs" variant="default" onClick={onCancel}>
            Cancel
          </Button>
          <Button size="xs" color="dark" loading={saving} disabled={!paidOn} onClick={() => void save()}>
            Mark paid
          </Button>
        </Group>
      </Box>
    </Stack>
  );
}
